<template>
  <DropdownMenuRoot
    v-model:open="isOpen"
    class="DropdownMenu"
    modal
  >
    <DropdownMenuTrigger ref="trigger" asChild>
      <slot name="trigger" />
    </DropdownMenuTrigger>

    <DropdownMenuPortal>
      <!-- Mobile overlay for compact variant -->
      <Transition name="overlay">
        <div v-if="isOpenOnMobile" class="DropdownMenu-overlay" />
      </Transition>

      <Transition name="fade">
        <DropdownMenuContent
          class="DropdownMenu-content"
          :align="isMobile ? 'start' : align"
          :alignOffset
          :sideOffset
          :side="position"
          :reference="dropdownMenuContentRef"
        >
          <header v-if="isMobile" class="DropdownMenu-header">
            <h5 v-if="title" class="DropdownMenu-title">
              {{ title }}
            </h5>

            <IconButton
              icon="window-close"
              class="DropdownMenu-close"
              size="md"
              :ariaLabel="t('ui.a11y.close')"
              @click="close"
            />
          </header>

          <template v-for="item of items" :key="item.id">
            <!-- Auto-separator (except first item) or manual separators -->
            <DropdownMenuSeparator v-if="item.separator" class="DropdownMenu-separator" />

            <DropdownMenuItem
              class="DropdownMenu-item"
              :disabled="item.disabled"
              asChild
              @select="selectItem(item)"
            >
              <slot name="item" :item="item">
                <DropdownItem :text="item.label" :leftIcon="item.icon" />
              </slot>
            </DropdownMenuItem>
          </template>
        </DropdownMenuContent>
      </Transition>
    </DropdownMenuPortal>
  </DropdownMenuRoot>
</template>

<script setup lang="ts">
import {
  DropdownMenuRoot,
  DropdownMenuTrigger,
  DropdownMenuPortal,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from 'reka-ui';
import { DropdownItem, IconButton } from '@cvo/ui';
import { computed, ref, useTemplateRef, watch } from 'vue';
import { useResponsive } from '@cvo/ui/composables';
import { useI18n } from '@cvo/i18n/composables/useI18n';
import { type DropdownMenuItem as DropdownMenuItemProps, type DropdownMenuProps } from './DropdownMenu.model';

withDefaults(defineProps<DropdownMenuProps>(), {
  align: 'end',
  alignOffset: 0,
  sideOffset: 0,
  position: 'bottom',
  separator: false,
  title: undefined,
});

const { t } = useI18n();
const { isMobile } = useResponsive();
const triggerElement = useTemplateRef<typeof DropdownMenuTrigger>('trigger');

const isOpen = ref(false);

const dropdownMenuContentRef = computed(() => (isMobile.value ? document.body : triggerElement.value?.$el));
const isOpenOnMobile = computed(() => isOpen.value && isMobile.value);

function selectItem(item: DropdownMenuItemProps) {
  item.action?.();
};

function close() {
  isOpen.value = false;
}

watch(isMobile, () => {
  close();
});
</script>

<style lang="scss">
@use '@cvo/ui/styles/design-system' as vars;
@use '@cvo/ui/styles/utils/layout';
@use '@cvo/ui/styles/utils/mixins' as mixins;
@use '@cvo/ui/styles/utils/responsive';

@layer cvo-ui {
  .DropdownMenu {
    &-overlay {
      @include mixins.set-overlay;
    }

    &-separator {
      height: 1px;
      background-color: vars.$color-neutral-gray-300;
    }

    &-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: vars.$dimensions-d-3;
      border-block-end: 1px solid vars.$color-neutral-gray-300;
    }

    &-title {
      color: vars.$organisms-card-details-title-color;
    }

    &-close {
      color: vars.$molecules-bottomsheet-menu-icon-color;
    }

    &-content {
      background-color: vars.$atoms-dropdown-baseitem-default-bg-color;
      border-radius: vars.$border-radius-md;
      overflow: hidden;
      box-shadow: vars.$shadow-md;
      min-width: 200px;
      max-width: 320px;

      @include responsive.mobile {
        min-width: 100vw;
        max-width: revert;
        position: fixed;
        bottom: 0;
        border-radius: vars.$border-radius-md vars.$border-radius-md 0 0;
        padding-bottom: vars.$dimensions-d-3;
      }

      @include responsive.tablet {
        min-width: 166px;
      }
    }
  }
}
</style>
